/**
 * 검색 URL = 유일한 검색 상태.
 *
 * 검색바·결과 페이지·지도가 각자 URL을 조립하면 보존하는 키가 어긋난다(X로 지운
 * 검색어가 필터 변경 때 되살아나고, place가 빠져 헤딩이 상위 도시로 돌아가고, 언어
 * 접두사가 빠진다). 그래서 파싱→전이→직렬화를 여기 한 곳에 두고, 호출자는 "어떤
 * 행동인가"만 말한다. 규칙표는 docs/superpowers/specs/2026-09-14-search-state-design.md.
 */

import { normalizeCategoryCodes } from "@/config/categories";

export type SearchScope =
  | "city"
  | "district"
  | "region"
  | "country"
  | "nearby"
  | "keyword"
  | "partner";

export interface SearchBounds {
  ne_lat: number;
  ne_lng: number;
  sw_lat: number;
  sw_lng: number;
}

export interface SearchIdentity {
  slug: string;
  scope: SearchScope;
  country?: string;
  /** scope=region 의 지역(admin1) 코드 — "JP-14" */
  region?: string;
  bounds?: SearchBounds | null;
  q?: string;
  place?: string;
  locationSearch?: "NEARBY" | "MAP_AREA";
}

export interface SearchFilters {
  categories: string[];
  usageMethod?: "pickup" | "delivery" | "shipping";
  deliveryCountry?: string;
  minPrice?: number;
  maxPrice?: number;
  checkin?: string;
  checkout?: string;
  sort?: string;
}

export interface SearchView {
  zoom?: number;
  clat?: number;
  clng?: number;
  t?: string;
}

export interface SearchUrlState {
  identity: SearchIdentity;
  filters: SearchFilters;
  view: SearchView;
  /** 의미를 해석하지 않고 그대로 나르는 키(user_lat/user_lng/search_type). */
  passthrough: Record<string, string>;
}

export type SearchAction =
  | { type: "newSearch"; identity: SearchIdentity }
  | { type: "clearKeyword" }
  | { type: "setFilters"; patch: Partial<SearchFilters> }
  | { type: "searchThisArea"; bounds: SearchBounds }
  | { type: "nearby"; bounds: SearchBounds };

const SCOPES: readonly SearchScope[] = ["city", "district", "region", "country", "nearby", "keyword", "partner"];
const USAGE: readonly NonNullable<SearchFilters["usageMethod"]>[] = ["pickup", "delivery", "shipping"];
// hub/station/model/bclass/maker/btype/ride/trans/party/lm/sheet = 지점 허브(모빌리티·캠핑)의 열린 시트·필터 상태(hub-url.ts).
// 검색 전이(searchThisArea 등)가 이 값을 떨어뜨리면 뒤로가기로 돌아왔을 때 시트가
// 풀리므로, 의미를 해석하지 않고 그대로 나르는 키에 포함한다.
/**
 * 지점 허브에서 열어 둔 지점·차종 시트(`station`·`model`)를 뗀다. 범위를 새로 잡는 이동
 * (이 지역에서 검색·내 주변)은 목록으로 돌아가야 한다 — 남겨 두면 허브가 URL 을 따라 그
 * 시트를 다시 열고 지도를 그 지점으로 되돌렸다(09-29). 탭·필터 축·명소는 유지.
 */
function withoutOpenHubSheet(passthrough: Record<string, string>): Record<string, string> {
  const { station: _s, model: _m, ...rest } = passthrough;
  void _s;
  void _m;
  return rest;
}

const PASSTHROUGH_KEYS = [
  "user_lat",
  "user_lng",
  "search_type",
  "hub",
  "station",
  "model",
  "bclass",
  "maker",
  "btype",
  "ride",
  "trans",
  "party",
  "lm",
  "sheet",
] as const;

type ParamSource = URLSearchParams | { get(k: string): string | null };

const num = (v: string | null): number | undefined => {
  if (v == null || v === "") return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};
const str = (v: string | null): string | undefined => (v == null || v === "" ? undefined : v);

/** `/{locale}/s/{slug}` 또는 `/s/{slug}`에서 slug를 꺼낸다. 없으면 "products". */
function slugFromPath(pathname: string): string {
  const segs = pathname.split("/").filter(Boolean);
  const i = segs.indexOf("s");
  const raw = i >= 0 ? segs[i + 1] : undefined;
  return raw ? decodeURIComponent(raw) : "products";
}

export function parseSearchUrl(pathname: string, params: ParamSource): SearchUrlState {
  const ne_lat = num(params.get("ne_lat"));
  const ne_lng = num(params.get("ne_lng"));
  const sw_lat = num(params.get("sw_lat"));
  const sw_lng = num(params.get("sw_lng"));
  const bounds: SearchBounds | null =
    ne_lat !== undefined && ne_lng !== undefined && sw_lat !== undefined && sw_lng !== undefined
      ? { ne_lat, ne_lng, sw_lat, sw_lng }
      : null;

  const scopeRaw = params.get("scope");
  // scope 없는 URL은 page.tsx의 legacyCanonicalPath → permanentRedirect로 스트리밍
  // 전에 308되므로, 클라이언트가 이 기본값을 실제로 만나지는 않는다. 순수 모듈을
  // 유지하려고 슬러그에서 scope를 추론하지 않는다(그러려면 지명 인덱스가 필요하다).
  const scope: SearchScope = (SCOPES as readonly string[]).includes(scopeRaw ?? "")
    ? (scopeRaw as SearchScope)
    : "keyword";
  const ls = params.get("location_search");

  const usageRaw = params.get("usage_method");
  const categoriesRaw = params.get("categories");

  const passthrough: Record<string, string> = {};
  for (const k of PASSTHROUGH_KEYS) {
    const v = params.get(k);
    if (v) passthrough[k] = v;
  }

  return {
    identity: {
      slug: slugFromPath(pathname),
      scope,
      country: str(params.get("country")),
      region: str(params.get("region")),
      bounds,
      q: str(params.get("q")) ?? str(params.get("search")),
      place: str(params.get("place")),
      locationSearch: ls === "NEARBY" || ls === "MAP_AREA" ? ls : undefined,
    },
    filters: {
      categories: categoriesRaw ? normalizeCategoryCodes(categoriesRaw.split(",").filter(Boolean)) : [],
      usageMethod: (USAGE as readonly string[]).includes(usageRaw ?? "")
        ? (usageRaw as SearchFilters["usageMethod"])
        : undefined,
      deliveryCountry: str(params.get("delivery_country")),
      minPrice: num(params.get("min_price")),
      maxPrice: num(params.get("max_price")),
      checkin: str(params.get("checkin")),
      checkout: str(params.get("checkout")),
      sort: str(params.get("sort")),
    },
    view: {
      zoom: num(params.get("zoom")),
      clat: num(params.get("clat")),
      clng: num(params.get("clng")),
      t: str(params.get("t")),
    },
    passthrough,
  };
}

/** 키 순서를 고정해 같은 상태면 같은 문자열이 나오게 한다(테스트·비교 안정). */
export function serializeSearchUrl(locale: string, state: SearchUrlState): string {
  const { identity: id, filters: f, view: v, passthrough } = state;
  const usp = new URLSearchParams();
  const set = (k: string, val: string | number | undefined | null) => {
    if (val === undefined || val === null || `${val}` === "") return;
    usp.set(k, `${val}`);
  };
  set("scope", id.scope);
  set("country", id.country);
  set("region", id.region);
  if (id.bounds) {
    set("ne_lat", id.bounds.ne_lat);
    set("ne_lng", id.bounds.ne_lng);
    set("sw_lat", id.bounds.sw_lat);
    set("sw_lng", id.bounds.sw_lng);
  }
  set("location_search", id.locationSearch);
  set("place", id.place);
  set("q", id.q);
  if (f.categories.length > 0) set("categories", f.categories.join(","));
  set("usage_method", f.usageMethod);
  set("delivery_country", f.deliveryCountry);
  set("min_price", f.minPrice);
  set("max_price", f.maxPrice);
  set("checkin", f.checkin);
  set("checkout", f.checkout);
  set("sort", f.sort);
  for (const k of PASSTHROUGH_KEYS) set(k, passthrough[k]);
  set("zoom", v.zoom);
  set("clat", v.clat);
  set("clng", v.clng);
  set("t", v.t);
  const qs = usp.toString();
  return `/${locale}/s/${encodeURIComponent(id.slug)}${qs ? `?${qs}` : ""}`;
}

/** 새 검색·내 주변에서 필터를 나를 때 지역 종속 값(delivery_country)은 버린다. */
function carriedFilters(f: SearchFilters): SearchFilters {
  const { deliveryCountry: _drop, ...rest } = f;
  void _drop;
  return { ...rest, categories: [...rest.categories] };
}

export function transition(
  state: SearchUrlState,
  action: SearchAction,
  now: number = Date.now(),
): SearchUrlState {
  switch (action.type) {
    case "newSearch":
      return {
        identity: { ...action.identity },
        filters: carriedFilters(state.filters),
        view: { t: String(now) },
        passthrough: {},
      };
    case "clearKeyword": {
      const { q: _q, ...identity } = state.identity;
      void _q;
      return {
        identity,
        filters: { ...state.filters, categories: [...state.filters.categories] },
        view: { ...state.view },
        passthrough: { ...state.passthrough },
      };
    }
    case "setFilters":
      return {
        identity: { ...state.identity },
        filters: { ...state.filters, ...action.patch, categories: [...(action.patch.categories ?? state.filters.categories)] },
        view: { ...state.view },
        passthrough: { ...state.passthrough },
      };
    case "searchThisArea": {
      const { place: _p, ...rest } = state.identity;
      void _p;
      return {
        identity: { ...rest, bounds: { ...action.bounds }, locationSearch: "MAP_AREA" },
        filters: { ...state.filters, categories: [...state.filters.categories] },
        view: {},
        passthrough: withoutOpenHubSheet(state.passthrough),
      };
    }
    case "nearby":
      return {
        identity: { slug: "nearby", scope: "nearby", bounds: { ...action.bounds }, locationSearch: "NEARBY" },
        filters: carriedFilters(state.filters),
        view: { t: String(now) },
        passthrough: withoutOpenHubSheet(state.passthrough),
      };
  }
}
